import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import API from "../../api/axiosInstance";
import "../css/Downloads.css";

const EXCEL_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Item ids that have a backend Excel export wired up.
 * Paths are relative segments; see getDownloadPath().
 */
const EXCEL_EXPORT_BY_ITEM_ID = {
  1: {
    route: "los-angeles-team",
    filename: "los_angeles_team.xlsx",
    downloadTitle: "Download Los Angeles Team Excel",
  },
  2: {
    route: "los-angeles-team-leaders",
    filename: "los_angeles_team_leaders.xlsx",
    downloadTitle: "Download Los Angeles Team Leaders Excel",
  },
  3: {
    route: "los-angeles-pending-nfs-nocs",
    filename: "los_angeles_pending_nfs_nocs.xlsx",
    downloadTitle: "Download Pending NFs/NOCs Excel",
  },
   4: {
    route: "nfs-emails",
    filename: "nfs_emails.xlsx",
    downloadTitle: "Download NFs Emails Excel",
  },
  5: {
    route: "all-xmas-card-details",
    filename: "all_xmas_card_details.xlsx",
    downloadTitle: "Download All Xmas Card Details Excel",
  },
  6: {
    route: "ceo-xmas-card-details",
    filename: "ceo_xmas_card_details.xlsx",
    downloadTitle: "Download CEO Xmas Card Details Excel",
  },
  7: {
  route: "all-nfs-full-export",
  filename: "all_nfs_full_export.xlsx",
  downloadTitle: "Download All NFs Full Export Excel",
},
8: {
  route: "all-xmas-nfs-emails",
  filename: "all_xmas_nfs_emails.xlsx",
  downloadTitle: "Download All XMAS + NFs Emails Excel",
},
9: {
  route: "all-pm-full-export",
  filename: "all_pm_full_export.xlsx",
  downloadTitle: "Download All PM Full Export Excel",
},
10: {
  route: "all-persons-main-roles-export",
  filename: "all_persons_main_roles_export.xlsx",
  downloadTitle: "Download All Persons Main Roles Export Excel",
},
};

function isExcelExportAvailable(itemId) {
  return Object.prototype.hasOwnProperty.call(EXCEL_EXPORT_BY_ITEM_ID, itemId);
}

/**
 * baseURL is often `http://localhost:5000/api` — path must be `/download/...`
 * not `/api/download/...` or axios becomes `/api/api/download/...` (HTML, not xlsx).
 */
function getDownloadPath(route) {
  const baseURL = API?.defaults?.baseURL || "";
  const hasApiPrefix = /\/api\/?$/i.test(String(baseURL).trim());
  return hasApiPrefix
    ? `/download/${route}`
    : `/api/download/${route}`;
}

/** .xlsx is a ZIP file; real exports start with "PK". */
async function assertLooksLikeXlsx(blob) {
  const head = await blob.slice(0, 4).arrayBuffer();
  const sig = new Uint8Array(head);
  const isPk = sig[0] === 0x50 && sig[1] === 0x4b;
  if (!isPk) {
    const text = await blob.slice(0, 500).text();
    throw new Error(
      text.trim().startsWith("<") || text.trim().startsWith("{")
        ? "Server returned HTML or JSON instead of an Excel file. Check API URL (avoid /api twice) and that the backend route exists."
        : "Downloaded file is not a valid Excel workbook."
    );
  }
}

async function downloadExcelExport(item) {
  const cfg = EXCEL_EXPORT_BY_ITEM_ID[item.id];
  if (!cfg) return;

  const path = getDownloadPath(cfg.route);
  console.log("[Downloads] Clicked item:", { id: item.id, title: item.title });
  console.log("[Downloads] Request path:", path);

  const response = await API.get(path, {
    responseType: "blob",
  });

  console.log("[Downloads] Response status:", response?.status);
  console.log("[Downloads] API response:", {
    status: response?.status,
    isBlob: response?.data instanceof Blob,
    type: response?.data?.type,
    size: response?.data?.size,
    contentType: response?.headers?.["content-type"],
  });

  if (response?.status !== 200) {
    throw new Error(`Unexpected status code: ${response?.status}`);
  }

  const raw =
    response?.data instanceof Blob
      ? response.data
      : new Blob([response?.data], { type: EXCEL_MIME });

  await assertLooksLikeXlsx(raw);

  const blob =
    raw.type === EXCEL_MIME ? raw : new Blob([raw], { type: EXCEL_MIME });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = cfg.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const Downloads = () => {
  const [busyId, setBusyId] = React.useState(null);
  const downloadBusy = busyId != null;

  const handleExcelClick = async (item) => {
    if (!isExcelExportAvailable(item.id)) return;

    setBusyId(item.id);
    try {
      await downloadExcelExport(item);
    } catch (e) {
      console.error(e);
      const msg =
        e?.message ||
        e?.response?.data?.message ||
        (typeof e?.response?.data === "string" ? e.response.data : null) ||
        "Download failed. Please try again.";
      window.alert(msg);
    } finally {
      setBusyId(null);
    }
  };

  const data = [
    {
      id: 1,
      title: "Team Los Angeles",
      desc: "NFs/NOCs Games planning teams for Los Angeles 2028 (names, positions and emails)",
    },
    {
      id: 2,
      title: "Team Los Angeles - Team Leaders",
      desc: "NFs/NOCs Games planning team leaders for Los Angeles 2028",
    },
    {
      id: 3,
      title: "Team Los Angeles - pending NFs/NOCs",
      desc: "Missing NFs/NOCs Games planning teams",
    },
    {
      id: 4,
      title: "NFs emails",
      desc: "Emails of all NFs (organisation, Secretary General and President)",
    },
     {
    id: 5,
    title: "All XMAS Cards emails",
    desc: "All XMAS Cards emails",
  },
  {
    id: 6,
    title: "All CEO XMAS Cards emails",
    desc: "All CEO XMAS Cards emails",
  },
  {
    id: 7,
    title: "All Nfs Full Export",
    desc: "All Nfs Full Export",
  },
  {
    id: 8,
    title: "All XMAS Cards emails + Emails of all NFs",
    desc: "All XMAS Cards emails + Emails of all NFs",
  },
  {
    id: 9,
    title: "All PM Full Export",
    desc: "All Professional Members Full Export",
  },
  {
    id: 10,
    title: "All Persons Main Roles Full Export",
    desc: "All Persons Main Roles Full Export",
  },
  ];

  return (
    <div className="downloads-page">

      <table className="downloads-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Description</th>
            <th className="text-center">Download</th>
          </tr>
        </thead>

        <tbody>
          {data.map((item) => {
            const exportAvailable = isExcelExportAvailable(item.id);

            return (
              <tr key={item.id}>
                <td>{item.id}</td>

                <td className="download-link">
                  {exportAvailable ? (
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0 text-start"
                      onClick={() => handleExcelClick(item)}
                      style={{
                        color: "inherit",
                        cursor: downloadBusy ? "wait" : "pointer",
                      }}
                      disabled={downloadBusy}
                      title={EXCEL_EXPORT_BY_ITEM_ID[item.id].downloadTitle}
                    >
                      {item.title}
                    </button>
                  ) : (
                    item.title
                  )}
                </td>

                <td>{item.desc}</td>

                <td className="text-center">
                  <button
                    type="button"
                    className="download-icon border-0 bg-transparent p-0"
                    title={
                      exportAvailable
                        ? "Download Excel"
                        : "Coming soon"
                    }
                    aria-label={
                      exportAvailable
                        ? EXCEL_EXPORT_BY_ITEM_ID[item.id].downloadTitle
                        : "Download not available"
                    }
                    disabled={!exportAvailable || downloadBusy}
                    onClick={() => handleExcelClick(item)}
                    style={{
                      cursor: exportAvailable ? "pointer" : "not-allowed",
                      opacity: exportAvailable ? 1 : 0.45,
                    }}
                  >
                    <FontAwesomeIcon icon={faFileExcel} className="download-icon" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Downloads;
